import React from 'react';
import { capitalizeString, convertCamelCaseToTitle } from '../../utils';
import Toggle from '../Toggle';
import {
  BottomsChart,
  MenBottomsChart,
  MenTopsChart,
  SizesByCountry,
  TopsChart,
  ShoesAndSocks,
  BraSizes,
  LengthGuide,
  bottomFitChart,
  onePieceFitChart,
  topFitChart,
  MenJacketsChart,
  JacketsChart,
  OnePiecesChart,
  SizeChart,
} from './sizeChartTypes';
import { supportedCountries } from './supportedCountries';
import { ITableRow } from './SizeTable';

export const generateTableDataForTopsAndBottoms = (
  data: SizesByCountry,
  measurementsData:
    | TopsChart
    | MenTopsChart
    | JacketsChart
    | MenJacketsChart
    | BottomsChart
    | MenBottomsChart
    | OnePiecesChart
    | topFitChart
    | bottomFitChart
    | onePieceFitChart,
  currentCountry: string,
  systemOfMeasurements: string
) => {
  let sizeTableData: { [key: string]: string }[] = [{ name: `US Size`, ...data.us }];

  const _currentCountry = currentCountry as keyof SizesByCountry;

  if (_currentCountry && _currentCountry !== 'us' && data[_currentCountry]) {
    sizeTableData = [
      {
        name: `${_currentCountry?.toUpperCase()} Size`,
        ...data[_currentCountry],
      },
      ...sizeTableData,
    ];
  }

  let measurementsTableData: { [key: string]: string }[] = [];
  if (measurementsData) {
    const lengthUnit = systemOfMeasurements === 'imperial' ? ' (in)' : ' (cm)';
    for (const [key, value] of Object.entries(measurementsData)) {
      measurementsTableData.push({
        name:
          key === 'cup'
            ? capitalizeString(convertCamelCaseToTitle(key))
            : capitalizeString(convertCamelCaseToTitle(key)) + lengthUnit,
        ...value,
      });
    }

    if (systemOfMeasurements !== 'imperial') {
      measurementsTableData.forEach((item) => {
        Object.entries(item).forEach((entry) => {
          const [key, value] = entry;
          if (key !== 'name') item[key] = convertInchesToCm(value);
        });
      });
    }
  }

  return [...sizeTableData, ...measurementsTableData] as ITableRow[];
};

export const generateTableDataForShoesAndSocks = (data: SizeChart, gender: string) => {
  const sizeTableData = Object.entries(data[gender as keyof typeof data].shoesAndSocks)
    .filter(([key]) => key !== 'us' && key !== 'cm')
    .map((entry) => {
      const [key, value] = entry;
      const name = key === 'aloSocks' ? 'Socks' : convertCamelCaseToTitle(key);
      return {
        name,
        ...value,
      };
    });
  // CM data for both men and women
  const sizeTableDataForMenInCM = generateTableDataForShoesAndSocksInCM(data['men'].shoesAndSocks, 'men');
  const sizeTableDataForWomenInCM = generateTableDataForShoesAndSocksInCM(data['women'].shoesAndSocks, 'women');
  const combinedSizeTableData = [...sizeTableData, ...sizeTableDataForMenInCM, ...sizeTableDataForWomenInCM];
  return combinedSizeTableData;
};

export const generateTableDataForShoesAndSocksInCM = (data: ShoesAndSocks | BraSizes, gender: string) => {
  const sizeTableData = Object.entries(data)
    .filter(([key]) => key === 'cm')
    .map((entry) => {
      const [key, value] = entry;
      return {
        name: key.toLocaleUpperCase() + ' - ' + convertCamelCaseToTitle(gender) + "'s",
        ...value,
      };
    });
  return sizeTableData as ITableRow[];
};
export const generateTableDataForBras = (data: BraSizes, currentCountry: string) => {
  const countriesKeys = supportedCountries.map((country) => country.value);
  let sizeTableData = Object.entries(data)
    .map((entry) => {
      const [key, value] = entry;
      return {
        name: convertCamelCaseToTitle(key).toUpperCase(),
        ...value,
      };
    })
    .filter((row) => !countriesKeys.includes(row.name.toLowerCase()));

  const _currentCountry = currentCountry as keyof SizesByCountry;

  if (_currentCountry && _currentCountry !== 'us') {
    sizeTableData = [
      {
        name: `${_currentCountry?.toUpperCase()} size`,
        ...data[_currentCountry],
      },
      ...sizeTableData,
    ];
  }

  return sizeTableData as ITableRow[];
};

export const generateHeaderRow = (
  data: SizesByCountry | ShoesAndSocks | BraSizes,
  systemOfMeasurements: string,
  toggleCallback?: (position: string) => void,
  toggle = true,
  headerRowTitle = '',
  shoesAndSocks?: boolean
) => {
  let defaultSizes = Object.keys(data.us).map((key) => key);
  if (shoesAndSocks) {
    defaultSizes = Object.values(data.us).map((key) => key);
  }
  const cells = defaultSizes.map((cell) => (
    <th key={cell} className={`header-cell`}>
      {cell}
    </th>
  ));
  const headerRows = toggle
    ? [
        <th className="toggle-cell" key="toggle">
          <Toggle
            label={`Switch system of measurements`}
            defaultPosition={systemOfMeasurements === 'metric' ? 'left' : 'right'}
            rightLabel="in"
            leftLabel="cm"
            onChange={toggleCallback && toggleCallback}
          />
        </th>,
        ...cells,
      ]
    : [headerRowTitle, ...cells];
  return headerRows;
};

export const generateHeaderRowForLengthGuide = (data: LengthGuide, headerRowTitle = '') => {
  const defaultSizes = Object.keys(data.inseam).map((key) => key);
  const cells = defaultSizes.map((cell) => (
    <th className={`header-cell`} key={cell}>
      {cell.replace('_', '/')}
    </th>
  ));
  const headerRows = [headerRowTitle, ...cells];
  return headerRows;
};

export const generateTableDataForLengthGuide = (data: LengthGuide, systemOfMeasurements: string) => {
  const lengthUnit = systemOfMeasurements === 'imperial' ? ' (in)' : ' (cm)';
  let sizeTableData: { [key: string]: string }[] = Object.entries(data).map((entry) => {
    const [key, value] = entry;
    return {
      name: capitalizeString(convertCamelCaseToTitle(key)) + lengthUnit,
      ...value,
    };
  });

  if (systemOfMeasurements !== 'imperial') {
    sizeTableData.forEach((item) => {
      Object.entries(item).forEach((entry) => {
        const [key, value] = entry;
        if (key !== 'name') item[key] = convertInchesToCm(value);
      });
    });
  }
  return sizeTableData as ITableRow[];
};

const convertInchesToCm = (valueRangeInInches: string) => {
  const valuesInInches = valueRangeInInches.replace(/\s/g, '').split('-');

  return valuesInInches
    .map((value) => {
      if (!isFinite(Number(value))) {
        return value;
      } else {
        return (Math.round(Number(value) * 2.54 * 2) / 2).toFixed(1);
      }
    })
    .join(' - ')
    .replaceAll('.0', '');
};

export const convertFeetRangeToCm = (valueRangeInFeet: string) => {
  const valueRangeArray = valueRangeInFeet.replace(/\s/g, '').split('-');
  const heightBreakdown = valueRangeArray.map((value) => {
    const [feet, inches] = value.split('’'); // ? [REVISIT] How reliable to split by ’ symbol??? Will the data be consistent?
    return { feet, inches };
  });
  const valueRangeInCm = heightBreakdown.map((item) => {
    const feetInCm = Number(Number(item.feet) * 30.84);
    const inchesInCm = Math.round(Number(item.inches) * 2.54 * 2) / 2;
    return +feetInCm.toFixed(0) + +inchesInCm.toFixed(0);
  });

  return valueRangeInCm.join(' - ');
};
